import React, { useState, useEffect } from 'react';
import { ChevronDown, Building2, Download, Calendar, AlertCircle, Landmark, Shield, Wallet } from 'lucide-react';
import { useDropdownData } from '../utils/useDropdownData';
import { useAuth, useActiveEstId } from './AuthContext';
import { payrollAPI } from '../api/apiService';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const ViewStatements = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [pfStatements, setPfStatements] = useState([]);
  const [esiStatements, setEsiStatements] = useState([]);
  const [bankStatements, setBankStatements] = useState([]);
  const [payStatements, setPayStatements] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    establishments,
    loading: dropdownLoading,
    errors,
    fetchEstablishments
  } = useDropdownData();

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lockedEstId) fetchEstablishments(); // ClientAdmin: skip, use locked est
  }, [fetchEstablishments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first establishment when establishments are loaded (SuperAdmin only)
  useEffect(() => {
    if (lockedEstId) return;
    if (establishments.length > 0 && !selectedEstablishment) {
      const firstEstablishmentId = establishments[0].id;
      handleEstablishmentChange({ target: { value: firstEstablishmentId } });
    }
  }, [establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEstablishmentChange = (e) => {
    const establishmentId = e.target.value;
    setSelectedEstablishment(establishmentId);
    setPfStatements([]);
    setEsiStatements([]);
    setBankStatements([]);
    setPayStatements([]);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const exportPayStatementToCSV = () => {
    if (!payStatements || payStatements.length === 0) {
      alert('No data to export');
      return;
    }

    // CSV headers matching the detailed pay statement table
    const headers = [
      'No',
      'NAME',
      'EID No',
      'FATHER NAME',
      'Desig',
      'UAN',
      'Basic (Original)',
      'Normal Days',
      'LOP Days',
      'Total/Payable Days',
      'WO',
      'Working Days',
      'Daily Wage (₹)',
      'Hourly Wage (₹)',
      'Basic (Computed)',
      'HRA',
      'Bonus',
      'U/Maint',
      'W/off/NH',
      'PF @ 13% (Employer)',
      'Total Earnings',
      'PF @ 12% (Employee)',
      'ESIC',
      'PT',
      'TDS',
      'LWF',
      'Health Insurance',
      'Advance Recovery',
      'Fines',
      'Damage Loss',
      'Other Deductions',
      'Total Ded',
      'Net Pay'
    ];

    // CSV rows
    const rows = payStatements.map((record, index) => {
      const lopDays = (parseFloat(record.lwp_days || 0) + parseFloat(record.absent_days || 0));
      const normalDays = record.wage_month === 2 ? 24 : 26;
      return [
        index + 1,
        `${record.first_name} ${record.last_name}`,
        record.employee_id,
        record.father_name || '',
        record.designation || '',
        record.uan || '',
        record.original_basic || 0,
        normalDays,
        lopDays,
        record.payable_days || record.present_days || 0,
        record.weekly_offs || 0,
        record.working_days || 0,
        parseFloat(record.daily_wage || 0).toFixed(2),
        parseFloat(record.hourly_wage || 0).toFixed(2),
        record.basic || 0,
        record.hra || 0,
        record.bonus || 0,
        record.uniform_maintenance || 0,
        record.week_off_nh || 0,
        record.pf_employer || (parseFloat(record.basic || 0) * 0.13),
        record.gross_wages || 0,
        record.pf || 0,
        record.esic || 0,
        record.pt || 0,
        record.tds || 0,
        record.lwf || 0,
        record.health_insurance || 0,
        record.advance_recovery || 0,
        record.fines || 0,
        record.damage_loss || 0,
        record.other_deductions || 0,
        record.total_deductions || 0,
        record.net_payable || 0
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pay_statement_${monthNames[selectedMonth - 1]}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Fetch actual payroll data from API
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (!selectedEstablishment) {
        setPfStatements([]);
        setEsiStatements([]);
        setBankStatements([]);
        setPayStatements([]);
        return;
      }

      try {
        setLoading(true);

        const params = {
          establishment_id: selectedEstablishment,
          month: selectedMonth,
          year: selectedYear
        };

        console.log('Fetching payroll data with params:', params);
        const response = await payrollAPI.getMonthly(params);
        console.log('Payroll API Response:', response);

        const payrollData = response.payroll || [];

        if (payrollData.length > 0) {
          // Set detailed pay statements
          setPayStatements(payrollData);

          // Generate PF statements from payroll data
          const pfData = payrollData.map(record => ({
            id: record.wage_id || record.id,
            employeeName: `${record.first_name} ${record.last_name}`,
            period: `${monthNames[selectedMonth - 1]} ${selectedYear}`,
            generatedOn: new Date().toISOString().split('T')[0],
            downloadUrl: '#',
            sendUrl: '#',
            uan: record.uan,
            basic: record.basic,
            employeePF: record.pf,
            employerPF: record.pf_employer || (parseFloat(record.basic || 0) * 0.13)
          }));
          setPfStatements(pfData);

          // Generate ESI statements from payroll data
          const esiData = payrollData.map(record => ({
            id: record.wage_id || record.id,
            employeeName: `${record.first_name} ${record.last_name}`,
            period: `${monthNames[selectedMonth - 1]} ${selectedYear}`,
            generatedOn: new Date().toISOString().split('T')[0],
            downloadUrl: '#',
            sendUrl: '#',
            employeeId: record.employee_id,
            grossWages: record.gross_wages,
            esiContribution: record.esic
          }));
          setEsiStatements(esiData);

          // Generate Bank statements from payroll data
          const bankData = payrollData.map(record => ({
            id: record.wage_id || record.id,
            employeeName: `${record.first_name} ${record.last_name}`,
            period: `${monthNames[selectedMonth - 1]} ${selectedYear}`,
            generatedOn: new Date().toISOString().split('T')[0],
            downloadUrl: '#',
            sendUrl: '#',
            employeeId: record.employee_id,
            bankAccount: record.bank_account || 'N/A',
            netPayable: record.net_payable
          }));
          setBankStatements(bankData);
        } else {
          setPfStatements([]);
          setEsiStatements([]);
          setBankStatements([]);
          setPayStatements([]);
        }

      } catch (error) {
        console.error('Error fetching payroll data:', error);
        setPfStatements([]);
        setEsiStatements([]);
        setBankStatements([]);
        setPayStatements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstablishment, selectedMonth, selectedYear]);

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <div>
            <h2 className="update-employee-title">View Statements</h2>
            <p className="update-employee-subtitle">View employee PF, ESI, and Bank statements</p>
          </div>
        </div>

        {/* Filters Section */}
        <div style={{
          background: 'var(--theme-background)',
          border: '1.5px solid rgba(26, 115, 232, 0.2)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>

          <div className="attendance-grid-filters">
            {/* Establishment Dropdown — hidden for ClientAdmin */}
            {!lockedEstId && (
            <div className="attendance-filter-group">
              <label className="attendance-filter-label">
                Organization
              </label>
              {dropdownLoading.establishments && (
                <div style={{ padding: '0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                  Loading organizations...
                </div>
              )}
              {errors.establishments && !dropdownLoading.establishments && (
                <div style={{
                  padding: '0.5rem',
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#991b1b',
                  fontSize: '0.75rem'
                }}>
                  <AlertCircle size={14} />
                  <span>Error: {errors.establishments}</span>
                </div>
              )}
              {!dropdownLoading.establishments && !errors.establishments && (
                <div style={{ position: 'relative', width: '100%', minWidth: '300px' }}>
                  <select
                    value={selectedEstablishment}
                    onChange={handleEstablishmentChange}
                    className="attendance-filter-select"
                    style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                  >
                    <option value="">Select Organization</option>
                    {establishments.map((est) => (
                      <option key={est.id} value={est.id}>
                        {est.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6b7280'
                  }} />
                </div>
              )}
            </div>
            )}

            {/* Month Dropdown */}
            <div className="attendance-filter-group">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper">
                  <Calendar size={15} />
                </span>
                Month
              </label>
              <div style={{ position: 'relative', width: '100%', minWidth: '200px' }}>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="attendance-filter-select"
                  style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                >
                  {monthNames.map((month, index) => (
                    <option key={index + 1} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280'
                }} />
              </div>
            </div>

            {/* Year Dropdown */}
            <div className="attendance-filter-group">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper">
                  <Calendar size={15} />
                </span>
                Year
              </label>
              <div style={{ position: 'relative', width: '100%', minWidth: '150px' }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="attendance-filter-select"
                  style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280'
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* PF Statement Table */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Shield size={20} />
            PF Statement
          </h3>
          {pfStatements.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: 'var(--theme-background)',
              border: '2px dashed #10b98150',
              borderRadius: '0.75rem',
              color: 'var(--theme-text-secondary)'
            }}>
              <Shield size={40} style={{ margin: '0 auto 0.5rem', opacity: 0.3, color: '#10b981' }} />
              <p style={{ fontSize: '0.875rem' }}>No PF statements available</p>
            </div>
          ) : (
            <div style={{
              background: 'var(--theme-background)',
              border: '1.5px solid #10b98150',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              maxHeight: '280px',
              overflowY: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#10b98115', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#10b981', background: '#10b98115' }}>Employee Name</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#10b981', background: '#10b98115' }}>Period</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#10b981', background: '#10b98115' }}>Generated On</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.8rem', color: '#10b981', background: '#10b98115' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pfStatements.map((statement) => (
                    <tr key={statement.id} style={{ borderTop: '1px solid #10b98130' }}>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.employeeName}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.period}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>{statement.generatedOn}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => window.open(statement.downloadUrl, '_blank')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#10b981',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            <Download size={12} />
                            Download
                          </button>
                          <button
                            onClick={() => alert('Send functionality coming soon')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#059669',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            Send
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ESI Statement Table */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Wallet size={20} />
            ESI Statement
          </h3>
          {esiStatements.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: 'var(--theme-background)',
              border: '2px dashed #3b82f650',
              borderRadius: '0.75rem',
              color: 'var(--theme-text-secondary)'
            }}>
              <Wallet size={40} style={{ margin: '0 auto 0.5rem', opacity: 0.3, color: '#3b82f6' }} />
              <p style={{ fontSize: '0.875rem' }}>No ESI statements available</p>
            </div>
          ) : (
            <div style={{
              background: 'var(--theme-background)',
              border: '1.5px solid #3b82f650',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              maxHeight: '280px',
              overflowY: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#3b82f615', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#3b82f6', background: '#3b82f615' }}>Employee Name</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#3b82f6', background: '#3b82f615' }}>Period</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#3b82f6', background: '#3b82f615' }}>Generated On</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.8rem', color: '#3b82f6', background: '#3b82f615' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {esiStatements.map((statement) => (
                    <tr key={statement.id} style={{ borderTop: '1px solid #3b82f630' }}>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.employeeName}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.period}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>{statement.generatedOn}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => window.open(statement.downloadUrl, '_blank')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#3b82f6',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            <Download size={12} />
                            Download
                          </button>
                          <button
                            onClick={() => alert('Send functionality coming soon')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#2563eb',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            Send
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bank Statement Table */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#8b5cf6',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Landmark size={20} />
            Bank Statement
          </h3>
          {bankStatements.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: 'var(--theme-background)',
              border: '2px dashed #8b5cf650',
              borderRadius: '0.75rem',
              color: 'var(--theme-text-secondary)'
            }}>
              <Landmark size={40} style={{ margin: '0 auto 0.5rem', opacity: 0.3, color: '#8b5cf6' }} />
              <p style={{ fontSize: '0.875rem' }}>No Bank statements available</p>
            </div>
          ) : (
            <div style={{
              background: 'var(--theme-background)',
              border: '1.5px solid #8b5cf650',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              maxHeight: '280px',
              overflowY: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#8b5cf615', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#8b5cf6', background: '#8b5cf615' }}>Employee Name</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#8b5cf6', background: '#8b5cf615' }}>Period</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', color: '#8b5cf6', background: '#8b5cf615' }}>Generated On</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.8rem', color: '#8b5cf6', background: '#8b5cf615' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bankStatements.map((statement) => (
                    <tr key={statement.id} style={{ borderTop: '1px solid #8b5cf630' }}>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.employeeName}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text)' }}>{statement.period}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>{statement.generatedOn}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => window.open(statement.downloadUrl, '_blank')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#8b5cf6',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            <Download size={12} />
                            Download
                          </button>
                          <button
                            onClick={() => alert('Send functionality coming soon')}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.7rem',
                              background: '#7c3aed',
                              color: 'var(--theme-surface, #ffffff)',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontWeight: '500'
                            }}
                          >
                            Send
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pay Statement - Detailed Payroll Table */}
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              margin: 0,
              color: '#0ea5e9',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Shield size={20} />
              Pay Statement - Detailed Payroll
            </h3>
            {payStatements.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={exportPayStatementToCSV}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    background: '#0ea5e9',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontWeight: '500'
                  }}
                >
                  <Download size={16} />
                  Download CSV
                </button>
                <button
                  onClick={() => alert('Send functionality coming soon')}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    background: '#0284c7',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontWeight: '500'
                  }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
          {payStatements.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: 'var(--theme-background)',
              border: '2px dashed #7dd3fc',
              borderRadius: '0.75rem',
              color: 'var(--theme-text-secondary)'
            }}>
              <Shield size={40} style={{ margin: '0 auto 0.5rem', opacity: 0.3, color: '#0ea5e9' }} />
              <p style={{ fontSize: '0.875rem' }}>No Pay statements available</p>
            </div>
          ) : (
            <div style={{
              background: 'var(--theme-surface, #ffffff)',
              borderRadius: '0.5rem',
              border: '1px solid #7dd3fc',
              overflowX: 'auto',
              overflowY: 'auto',
              maxWidth: '100%',
              maxHeight: '280px',
              WebkitOverflowScrolling: 'touch'
            }}>
              <table style={{
                width: '100%',
                minWidth: '2700px',
                borderCollapse: 'collapse',
                fontSize: '0.8rem'
              }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  {/* Main Category Header */}
                  <tr style={{ background: '#0ea5e9', position: 'sticky', top: 0, zIndex: 10 }}>
                    <th colSpan={6} style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--theme-surface, #ffffff)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid #0284c7',
                      borderRight: '2px solid white'
                    }}>Employee Details</th>
                    <th colSpan={8} style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--theme-surface, #ffffff)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid #0284c7',
                      borderRight: '2px solid white'
                    }}>Days & Wages</th>
                    <th colSpan={7} style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--theme-surface, #ffffff)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid #0284c7',
                      borderRight: '2px solid white'
                    }}>Earnings</th>
                    <th colSpan={11} style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--theme-surface, #ffffff)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid #0284c7',
                      borderRight: '2px solid white'
                    }}>Deductions</th>
                    <th style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: 'var(--theme-surface, #ffffff)',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid #0284c7'
                    }}>Net</th>
                  </tr>
                  {/* Sub Headers */}
                  <tr style={{ background: '#e0f2fe', position: 'sticky', top: '41px', zIndex: 10 }}>
                    {/* Employee Details */}
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>No</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>NAME</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>EID No</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>FATHER NAME</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Desig</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '2px solid #0ea5e9', color: '#075985' }}>UAN</th>
                    {/* Days & Wages */}
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Basic</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Normal</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Lve</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Total</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>WO</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Work<br/>Days</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Daily<br/>Wage</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '2px solid #0ea5e9', color: '#075985' }}>Hourly<br/>Wage</th>
                    {/* Earnings */}
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Basic</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>HRA</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Bonus</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>U/Maint</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>W/off/NH</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>PF@13%<br/>(ER)</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '700', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '2px solid #0ea5e9', color: '#0c4a6e' }}>Total</th>
                    {/* Deductions */}
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>PF@12%<br/>(EE)</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.65rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>ESIC</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>PT</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>TDS</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>LWF</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.6rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Health<br/>Ins</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.6rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Adv<br/>Recov</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Fines</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.6rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Damage<br/>Loss</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '600', fontSize: '0.6rem', borderBottom: '1px solid #bae6fd', borderRight: '1px solid #bae6fd', color: '#075985' }}>Other<br/>Ded</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: '700', fontSize: '0.7rem', borderBottom: '1px solid #bae6fd', borderRight: '2px solid #0ea5e9', color: '#0c4a6e' }}>Total Ded</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', fontSize: '0.75rem', borderBottom: '1px solid #bae6fd', color: '#0c4a6e' }}>Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {payStatements.map((record, index) => (
                    <tr
                      key={record.id}
                      style={{
                        background: index % 2 === 0 ? '#f0f9ff' : 'var(--theme-surface, #ffffff)',
                        borderBottom: '1px solid #bae6fd'
                      }}
                    >
                      {/* Employee Details */}
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#0369a1', fontSize: '0.8rem', borderRight: '1px solid #bae6fd' }}>{index + 1}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#0c4a6e', fontSize: '0.8rem', fontWeight: '600', borderRight: '1px solid #bae6fd' }}>{record.first_name} {record.last_name}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#0369a1', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>{record.employee_id}</td>
                      <td style={{ padding: '8px 10px', color: '#0369a1', fontSize: '0.8rem', borderRight: '1px solid #bae6fd' }}>{record.father_name || '-'}</td>
                      <td style={{ padding: '8px 10px', color: '#0369a1', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>{record.designation || '-'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#0369a1', fontSize: '0.7rem', borderRight: '2px solid #7dd3fc' }}>{record.uan || '-'}</td>
                      {/* Days & Wages */}
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.original_basic || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#075985', fontSize: '0.8rem', borderRight: '1px solid #bae6fd' }}>{record.wage_month === 2 ? 24 : 26}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: (parseFloat(record.lwp_days || 0) + parseFloat(record.absent_days || 0)) > 0 ? '#dc2626' : '#075985', fontSize: '0.8rem', fontWeight: (parseFloat(record.lwp_days || 0) + parseFloat(record.absent_days || 0)) > 0 ? '600' : '400', borderRight: '1px solid #bae6fd' }}>{parseFloat(record.lwp_days || 0) + parseFloat(record.absent_days || 0)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#075985', fontSize: '0.8rem', fontWeight: '600', borderRight: '1px solid #bae6fd' }}>{record.payable_days || record.present_days || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#075985', fontSize: '0.8rem', borderRight: '1px solid #bae6fd' }}>{record.weekly_offs || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'center', color: '#059669', fontSize: '0.8rem', fontWeight: '600', borderRight: '1px solid #bae6fd', background: '#ecfdf5' }}>{record.working_days || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#0369a1', fontSize: '0.75rem', fontWeight: '500', borderRight: '1px solid #bae6fd', background: '#f0f9ff' }}>₹{parseFloat(record.daily_wage || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#0369a1', fontSize: '0.75rem', fontWeight: '500', borderRight: '2px solid #7dd3fc', background: '#f0f9ff' }}>₹{parseFloat(record.hourly_wage || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      {/* Earnings */}
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.basic || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.hra || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.bonus || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.uniform_maintenance || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.week_off_nh || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.pf_employer || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#059669', fontSize: '0.8rem', fontWeight: '600', borderRight: '2px solid #7dd3fc', background: '#d1fae5' }}>₹{parseFloat(record.gross_wages || 0).toLocaleString('en-IN')}</td>
                      {/* Deductions */}
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.pf || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.esic || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.pt || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.tds || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.lwf || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.health_insurance || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.advance_recovery || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.fines || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.damage_loss || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#075985', fontSize: '0.75rem', borderRight: '1px solid #bae6fd' }}>₹{parseFloat(record.other_deductions || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#b91c1c', fontSize: '0.8rem', fontWeight: '600', borderRight: '2px solid #7dd3fc', background: '#fecaca' }}>₹{parseFloat(record.total_deductions || 0).toLocaleString('en-IN')}</td>
                      {/* Net Pay */}
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#047857', fontSize: '0.85rem', fontWeight: '700', background: '#a7f3d0' }}>₹{parseFloat(record.net_payable || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStatements;
